import React from 'react';

import AdvanceRequestSkeleton from '@components/Skeleton/AdvanceRequestSkeleton';
import DashboardSkeleton from '@components/Skeleton/DashboardSkeleton';
import DelegationsSkeleton from '@components/Skeleton/DelegationsSkeleton';
import DelegationsUpsertSkeleton from '@components/Skeleton/DelegationsUpsertSkeleton';
import { HistoryActionsSkeleton } from '@components/Skeleton/HistoryActionsSkeleton';
import ImportCreateSkeleton from '@components/Skeleton/ImportCreateSkeleton';
import ImportIndexSkeleton from '@components/Skeleton/ImportIndexSkeleton';
import ImportShowSkeleton from '@components/Skeleton/ImportShowSkeleton';
import { NavRoute } from '@data/models/navigation.interface';
import { User } from '@data/models/user.interface';
import { Permissions } from 'constants/permissions';
import { Routes } from 'constants/routes';

import { ScudRoles } from '@/web/src/constants/roles';

const DashboardRouter = React.lazy(() => import('@pages/Home/DashboardRouter'));
const TimesheetDetails = React.lazy(
  () => import('@pages/Timesheet/TimesheetDetails'),
);

const HistoryList = React.lazy(
  () => import('@pages/HistoryActions/HistoryList'),
);
const Delegations = React.lazy(() => import('@pages/Delegations/Index'));
const DelegationCreate = React.lazy(() => import('@pages/Delegations/Create'));
const DelegationShow = React.lazy(() => import('@pages/Delegations/Show'));
const AdvanceRequestList = React.lazy(
  () => import('@pages/AdvanceRequest/List/AdvanceRequestList'),
);
const AdvanceRequestHistory = React.lazy(
  () => import('@pages/AdvanceRequest/History/AdvanceRequestHistory'),
);
const AdvanceRequestManage = React.lazy(
  () => import('@pages/AdvanceRequest/Manage/AdvancePeriodList'),
);
const AdvanceRequestManageEdit = React.lazy(
  () => import('@pages/AdvanceRequest/Manage/AdvancePeriodEdit'),
);
const NotionPage = React.lazy(() => import('@pages/NotionPage/Index'));
const ImportIndex = React.lazy(() => import('@pages/Import/Index'));
const ImportShow = React.lazy(() => import('@pages/Import/Show'));
const ImportMapperPage = React.lazy(() => import('@pages/Import/MapperPage'));
const ImportCreate = React.lazy(() => import('@pages/Import/Create'));

const SuperAdmin = React.lazy(() => import('@pages/SuperAdmin/SuperAdmin'));
const Monitoring = React.lazy(() => import('@pages/Monitoring/Index'));
const AdminUsersIndex = React.lazy(() => import('@pages/AdminUsers/Index'));
const AdminUsersShow = React.lazy(() => import('@pages/AdminUsers/Show'));
const MissingAgenciesIndex = React.lazy(
  () => import('@pages/MissingAgencies/Index'),
);

const ReferentialsList = React.lazy(
  () => import('@pages/Referentials/ReferentialsList'),
);

const ReferentialDetails = React.lazy(
  () => import('@pages/Referentials/ReferentialDetails'),
);

export const defaultRoutes: NavRoute[] = [
  {
    name: 'navigation.links.dashboard',
    path: Routes.DASHBOARD_PATH,
    component: <DashboardRouter />,
    skeleton: <DashboardSkeleton />,
    permissions: [Permissions.Timesheets.List],
  },
  {
    name: 'navigation.links.timesheet',
    path: '/timesheets/:timesheetUuid',
    component: <TimesheetDetails />,
    permissions: [Permissions.Timesheets.List],
  },
  {
    name: 'navigation.links.history_actions',
    path: Routes.HISTORY_ACTIONS_PATH,
    component: <HistoryList />,
    skeleton: <HistoryActionsSkeleton />,
    permissions: [Permissions.HistoryActions.List],
  },
  {
    name: 'navigation.links.advance_manage_request',
    path: Routes.ADVANCE_REQUEST_LIST_PATH,
    component: <AdvanceRequestList />,
    skeleton: <AdvanceRequestSkeleton />,
    exact: true,
    permissions: [
      Permissions.AdvanceRequest.List,
      Permissions.AdvanceRequest.Create,
    ],
  },
  {
    name: 'navigation.links.advance_history',
    path: Routes.ADVANCE_REQUEST_HISTORY_PATH,
    component: <AdvanceRequestHistory />,
    skeleton: <AdvanceRequestSkeleton page="history" />,
    permissions: [Permissions.AdvanceRequest.List],
  },
  {
    name: 'navigation.links.advance_manage_unavailabilities',
    path: Routes.ADVANCE_REQUEST_MANAGE_PATH,
    component: <AdvanceRequestManage />,
    skeleton: <AdvanceRequestSkeleton page="manage" />,
    exact: true,
    permissions: [
      Permissions.AdvancePeriods.Create,
      Permissions.AdvancePeriods.Delete,
    ],
  },
  {
    name: 'navigation.links.advance_manage_unavailabilities_details',
    path: `${Routes.ADVANCE_REQUEST_MANAGE_PATH}/:advancePeriodId`,
    component: <AdvanceRequestManageEdit />,
    skeleton: <AdvanceRequestSkeleton page="manage" />,
    permissions: [
      Permissions.AdvancePeriods.Create,
      Permissions.AdvancePeriods.Delete,
    ],
  },
  {
    name: 'navigation.links.notion_page',
    path: Routes.NOTION_PATH + '/:id/:childId',
    component: <NotionPage />,
    skeleton: <HistoryActionsSkeleton />,
  },
  {
    name: 'navigation.links.notion_page',
    path: Routes.NOTION_PATH + '/:id',
    component: <NotionPage />,
    skeleton: <HistoryActionsSkeleton />,
  },
  {
    name: 'navigation.links.delegations',
    path: Routes.DELEGATIONS_PATH,
    component: <Delegations />,
    skeleton: <DelegationsSkeleton />,
    permissions: [Permissions.Delegation.ListAll],
  },
  {
    name: 'navigation.links.delegation',
    path: Routes.DELEGATIONS_SHOW_PATH + '/:delegationId',
    component: <DelegationShow />,
    skeleton: <DelegationsUpsertSkeleton />,
  },
  {
    name: 'navigation.links.delegations_create',
    path: Routes.DELEGATIONS_CREATE_PATH,
    component: <DelegationCreate />,
    skeleton: <DelegationsUpsertSkeleton />,
  },
  {
    name: 'navigation.links.import_show',
    path: Routes.IMPORT_INDEX_PATH + '/:importId',
    component: <ImportShow />,
    skeleton: <ImportShowSkeleton />,
    excludedRoles: [ScudRoles.NinaAgency],
  },
  {
    name: 'navigation.links.import_mapping',
    path: Routes.IMPORT_MAPPING_PATH,
    component: <ImportMapperPage />,
    skeleton: <ImportShowSkeleton />,
    excludedRoles: [ScudRoles.NinaAgency],
  },
  {
    name: 'navigation.links.import_index',
    path: Routes.IMPORT_INDEX_PATH,
    component: <ImportIndex />,
    skeleton: <ImportIndexSkeleton />,
    excludedRoles: [ScudRoles.NinaAgency],
  },
  {
    name: 'navigation.links.import_create',
    path: Routes.IMPORT_CREATE_PATH,
    component: <ImportCreate />,
    skeleton: <ImportCreateSkeleton />,
    excludedRoles: [ScudRoles.NinaAgency],
  },
  {
    name: 'navigation.links.super_admin',
    path: Routes.SUPER_ADMIN,
    component: <SuperAdmin />,
    roles: [ScudRoles.NinaSuperAdmin],
    permissions: [Permissions.Timesheets.LaunchMatchingContract],
  },
  {
    name: 'navigation.links.referentials',
    path: Routes.REFERENTIALS_PATH,
    component: <ReferentialsList />,
    excludedRoles: [ScudRoles.NinaAgency],
  },
  {
    name: 'navigation.links.referential_details',
    path: `${Routes.REFERENTIALS_PATH}/:clientReferentialId`,
    component: <ReferentialDetails />,
    excludedRoles: [ScudRoles.NinaAgency],
  },
  {
    name: 'navigation.links.monitoring',
    path: Routes.MONITORING_PATH,
    component: <Monitoring />,
    roles: [ScudRoles.NinaSuperAdmin],
  },
  {
    name: 'navigation.links.admin_users',
    path: Routes.ADMIN_USERS_PATH,
    component: <AdminUsersIndex />,
    roles: [ScudRoles.NinaResponsableCG, ScudRoles.NinaSuperAdmin],
  },
  {
    name: 'navigation.links.admin_user_show',
    path: `${Routes.ADMIN_USERS_PATH}/:profilAd`,
    component: <AdminUsersShow />,
    roles: [ScudRoles.NinaResponsableCG, ScudRoles.NinaSuperAdmin],
  },
  {
    name: 'navigation.links.missing_agencies',
    path: Routes.MISSING_AGENCIES_PATH,
    component: <MissingAgenciesIndex />,
    roles: [ScudRoles.NinaResponsableCG, ScudRoles.NinaSuperAdmin],
  },
];

export const getRouteByName = (name: string): NavRoute => {
  const routefound = defaultRoutes.find((route) => route.name === name);
  if (!routefound) {
    throw Error(`La route ${name} demandée à getRouteByName n'existe pas`);
  }

  return routefound;
};

export const userHasPermission = (user: User, route: NavRoute): boolean =>
  !route.permissions?.length ||
  route.permissions.some((permission) => user.permissions.includes(permission));

export const userHasRole = (user: User, route: NavRoute): boolean =>
  !route.roles?.length || route.roles.some((role) => user.roles.includes(role));

export const userIsNotExcluded = (user: User, route: NavRoute): boolean =>
  !route.excludedRoles?.some((role) => user.roles.includes(role));

export const userCanSeeRoute = (user: User, route: NavRoute): boolean => {
  return (
    userHasPermission(user, route) &&
    userHasRole(user, route) &&
    userIsNotExcluded(user, route)
  );
};
